import { useState, useRef, useContext, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Select, MenuItem,
    FormControl, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, Divider, Paper,
    Checkbox, FormControlLabel, Fab, Zoom,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AuthContext } from '../../utils/AuthContext';
import { getRates } from '../../services/QuotationService';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import CustomSelect2 from '../CustomSelect2';
import NotesDisplay from '../NotesDisplay';
import RatesVersionSelect from '../RatesVersionSelect';
import PercentInput from '../PercentInput';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { LEAVE_BLANK, BLANK_QUOTE_RATES, BlankQuoteNotice, applyLeaveBlank, isBlankQuoteRates, isQuoteBlankForPlan } from './leaveBlank';
import { ManualEditNotice, withFreshRatesFlag } from './manualPremiumEdits';

// premier/elite/ultimate each have a "with maternity" variant rate; select and the dental
// add-ons (which BHP_Indonesia_Rates.js itself keeps commented out of its plan mapping —
// they aren't part of the supported Add Plan flow yet) do not.
const PLANS = [
    { id: 'select', label: 'Select', hasMaternity: false },
    { id: 'premier', label: 'Premier', hasMaternity: true },
    { id: 'elite', label: 'Elite', hasMaternity: true },
    { id: 'ultimate', label: 'Ultimate', hasMaternity: true },
];

const PAYMENT_FREQUENCIES = ['Annually', 'Semi Annually', 'Quarterly', 'Monthly'];
const BRAND_COLOR = '#0c1b4e';
const MATERNITY_ROW_COLOR = '#0076bd';

function getCurrencySign(currency) {
    if (currency === 'USD') return '$';
    if (currency === 'SGD') return 'S$';
    if (currency === 'EURO') return '€';
    if (currency === 'GBP') return '£';
    return '';
}

function formatRate(rate, currency) {
    if (rate === null || rate === undefined) return '-';
    if (typeof rate === 'string') return rate;
    if (isNaN(rate)) return 'Unavailable';
    return getCurrencySign(currency) + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Same as BHP_Indonesia_Rates: discount/loading scale the plan rate
function getRateFactor(discount, loading) {
    return (100 - (parseFloat(discount) || 0) + (parseFloat(loading) || 0)) / 100;
}

// Same default rule as BHP_Indonesia_Rates.js's checkAddOnPerClient('maternity', ...):
// females aged 18-64 default to maternity included, everyone else defaults to excluded.
function defaultMaternity(client) {
    return client?.gender === 'Female' && client?.age >= 18 && client?.age <= 64;
}

export default function BHP_Indonesia_Compact_Rates_Dialog({ open, onClose, appConfig, quotes = [], clients = [], quotations = [], onAddToQuotations, editingPlan = null, isStale = false, insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, onRatesVersionChanged }) {
    const { auth } = useContext(AuthContext);

    const areaOptions = appConfig?.content?.bhp_indonesia_area_of_coverage || [];
    const defaultArea = areaOptions.includes('Worldwide excl USA') ? 'Worldwide excl USA' : (areaOptions[0] || '');
    const currencyOptions = appConfig?.content?.currencies || [];
    const getDeductibles = (curr) => appConfig?.content?.deductibles?.bhp_indonesia?.[curr] || [];

    const copayOptions = appConfig?.content?.specifics?.bhp_indonesia?.copay_options || [];
    const groupSizes = appConfig?.content?.specifics?.bhp_indonesia?.group_sizes || {};
    const groupSizeOptions = Object.keys(groupSizes);

    const [selectedPlan, setSelectedPlan] = useState('select');
    const [areaOfCoverage, setAreaOfCoverage] = useState(defaultArea);
    const [currency, setCurrency] = useState('USD');
    const [paymentFrequency, setPaymentFrequency] = useState('Annually');
    const [copay, setCopay] = useState(copayOptions[0] || '');
    const [groupSize, setGroupSize] = useState(groupSizeOptions[0] || '');
    const [discount, setDiscount] = useState('0');
    const [loadingPercent, setLoadingPercent] = useState('0');
    // { [clientIndex]: boolean } — only meaningful for premier/elite/ultimate
    const [maternityPerClient, setMaternityPerClient] = useState({});
    // { [quoteName]: deductible string }
    const [quoteDeductibles, setQuoteDeductibles] = useState({});
    const [deductibleErrors, setDeductibleErrors] = useState({});
    const [ratesData, setRatesData] = useState(null);
    // Set once View Rates fetched new rates, which replace any premiums edited by hand.
    const fetchedFreshRatesRef = useRef(false);
    // Rates were shown but an option that changes the fetch was edited since
    const [ratesStale, setRatesStale] = useState(false);
    const [loading, setLoading] = useState(false);
    const initializingFromEdit = useRef(false);
    const ratesEndRef = useRef(null);

    // Clear rates that no longer match the selected options, offering a refresh if any were shown
    function invalidateRates() {
        if (ratesData) setRatesStale(true);
        setRatesData(null);
    }

    useEffect(() => {
        if (ratesData) {
            ratesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [ratesData]);

    const selectedPlanObj = PLANS.find(p => p.id === selectedPlan) || PLANS[0];

    function isMaternityIncluded(clientIndex) {
        const isChecked = maternityPerClient[clientIndex];
        if (isChecked === undefined) return defaultMaternity(clients[clientIndex]);
        return isChecked;
    }

    function getDisplayValue(property) {
        const value = groupSizes?.[groupSize]?.[property];
        if (!value) return 'N/A';
        if (isNaN(value)) return value;
        return (value * 100) + '%';
    }

    // Pre-populate form when opening in edit mode
    useEffect(() => {
        // A stale plan (a client or quote added since it was quoted) opens with the refresh offer showing.
        setRatesStale(open && !!editingPlan && isStale);
        fetchedFreshRatesRef.current = false;
        if (!open) {
            setRatesData(null);
            return;
        }
        if (open && editingPlan) {
            initializingFromEdit.current = true;
            const planId = (editingPlan.plan_id || '').replace('bhp_indonesia_', '');
            const editingPlanObj = PLANS.find(p => p.id === planId) || PLANS[0];
            setSelectedPlan(planId);
            setAreaOfCoverage(editingPlan.payloads?.area_of_coverage || defaultArea);
            setCurrency(editingPlan.payloads?.currency || 'USD');
            setPaymentFrequency(editingPlan.payloads?.payment_frequency || 'Annually');
            setCopay(editingPlan.payloads?.copay || copayOptions[0] || '');
            setGroupSize(editingPlan.payloads?.group_size || groupSizeOptions[0] || '');
            const savedDiscount = editingPlan.payloads?.discount ?? '0';
            const savedLoading = editingPlan.payloads?.loading ?? '0';
            setDiscount(String(savedDiscount));
            setLoadingPercent(String(savedLoading));
            const savedFactor = getRateFactor(savedDiscount, savedLoading);
            // Undo the saved discount/loading so ratesData holds the raw rate again
            const unscale = val => (typeof val === 'number' && savedFactor > 0 ? val / savedFactor : val);
            const deductibles = {};
            (quotes || []).forEach(quote => {
                deductibles[quote.name] = editingPlan.deductibles_per_quote?.[quote.uid] || '';
            });
            setQuoteDeductibles(deductibles);

            // Reconstruct ratesData from existing quotations — only the maternity variant that
            // was actually saved per client is recoverable; the other variant shows as
            // unavailable until "View Rates" is re-run.
            const ratesMap = {};
            const maternityMap = {};
            (quotes || []).forEach(quote => {
                const quotation = (quotations || []).find(q => q.uid === quote.uid);
                if (!quotation) return;
                if (isQuoteBlankForPlan(quotation, editingPlan)) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                const matchesEditingPlan = p => editingPlan.uid ? p.plan_uid === editingPlan.uid : p.plan_id === editingPlan.plan_id;
                const rates = clients.map((client, ci) => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    const premium = clientPremium?.premiums?.find(matchesEditingPlan);
                    const val = unscale(premium?.premium_rate ?? null);
                    const withMaternity = !!premium?.with_maternity;
                    maternityMap[ci] = withMaternity;
                    const fieldKey = editingPlanObj.hasMaternity && withMaternity ? `${planId}_with_maternity` : planId;
                    return { [fieldKey]: val };
                });
                ratesMap[quote.uid] = { rates };
            });
            setMaternityPerClient(maternityMap);
            setRatesData(Object.keys(ratesMap).length > 0 ? ratesMap : null);
            setTimeout(() => { initializingFromEdit.current = false; }, 0);
        } else if (open) {
            setSelectedPlan('select');
            setAreaOfCoverage(defaultArea);
            setCurrency('USD');
            setPaymentFrequency('Annually');
            setCopay(copayOptions[0] || '');
            setGroupSize(groupSizeOptions[0] || '');
            setDiscount('0');
            setLoadingPercent('0');
            setMaternityPerClient({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Reset deductible when currency changes (skip when initializing from edit)
    useEffect(() => {
        if (initializingFromEdit.current) return;
        const defaults = {};
        (quotes || []).forEach(q => {
            defaults[q.name] = getDeductibles(currency)[0] || '';
        });
        setQuoteDeductibles(defaults);
        invalidateRates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currency]);

    // Quotes set to "Leave Blank" skip rating and render as empty columns in the preview
    function isBlankQuote(quote) {
        return quoteDeductibles[quote.name] === LEAVE_BLANK;
    }

    async function handleViewRates() {
        const errors = {};
        (quotes || []).forEach(q => {
            if (!quoteDeductibles[q.name]) errors[q.name] = true;
        });
        if (Object.keys(errors).length > 0) {
            setDeductibleErrors(errors);
            const first = (quotes || []).find(q => errors[q.name]);
            toast.error(`Please select a deductible for "${first?.name}"`, { hideProgressBar: true, autoClose: 3000 });
            return;
        }
        setDeductibleErrors({});

        setLoading(true);
        setRatesData(null);
        setRatesStale(false);

        try {
            const results = await Promise.all(
                (quotes || []).map(quote => isBlankQuote(quote)
                    ? Promise.resolve({ quoteUid: quote.uid, quote, blank: true })
                    : getRates({
                        token: auth.token,
                        body: {
                            clients,
                            deductibles: { bhp_indonesia: quoteDeductibles[quote.name] || '' },
                            insurers: ['bhp_indonesia'],
                            specifics: { bhp_indonesia: { copay, group_size: groupSize } },
                            specific_area_of_coverage: { bhp_indonesia: areaOfCoverage },
                            rates_version_per_insurer: { bhp_indonesia: selectedRatesVersion?.bhp_indonesia || '' },
                            specific_currency: { bhp_indonesia: currency },
                            specific_payment_frequency: { bhp_indonesia: paymentFrequency },
                        },
                    }).then(response => ({ quoteUid: quote.uid, data: response?.data?.bhp_indonesia }))
                )
            );

            const ratesMap = {};
            results.forEach(({ quoteUid, data, blank }) => {
                if (blank) {
                    ratesMap[quoteUid] = BLANK_QUOTE_RATES;
                    return;
                }
                ratesMap[quoteUid] = { rates: data?.rates || [] };
            });
            setRatesData(ratesMap);
            fetchedFreshRatesRef.current = true;
        } catch (err) {
            console.error('Error fetching BHP Indonesia rates:', err);
            toast.error('Error fetching rates', { hideProgressBar: true, autoClose: 3000 });
        }

        setLoading(false);
    }

    const rateFactor = getRateFactor(discount, loadingPercent);

    // Reads whichever variant (base / with-maternity) applies for this client from the raw
    // per-client rate row fetched from the API, with discount/loading applied.
    function getRate(clientIndex, quoteUid) {
        const row = ratesData?.[quoteUid]?.rates?.[clientIndex];
        if (!row) return null;
        const fieldKey = selectedPlanObj.hasMaternity && isMaternityIncluded(clientIndex)
            ? `${selectedPlan}_with_maternity`
            : selectedPlan;
        const val = row[fieldKey];
        if (val === undefined) return null;
        return typeof val === 'number' ? val * rateFactor : val;
    }

    function getSubtotal(quoteUid) {
        if (!ratesData?.[quoteUid]?.rates) return null;
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = getRate(i, quoteUid);
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    function handleAddToQuotations() {
        if (!ratesData || !onAddToQuotations) return;
        // plan_id identifies the tier and is shared by every instance of it; uid identifies this
        // specific plan instance so multiple plans of the same tier can coexist instead of
        // overwriting each other. Editing reuses the existing plan's uid.
        const planUid = editingPlan ? editingPlan.uid : uuidv4();
        const updates = [];
        (quotes || []).forEach(quote => {
            clients.forEach((client, clientIndex) => {
                const rate = getRate(clientIndex, quote.uid);
                const withMaternity = selectedPlanObj.hasMaternity && isMaternityIncluded(clientIndex);
                updates.push({
                    quoteUid: quote.uid,
                    clientUid: client.uid,
                    planId: `bhp_indonesia_${selectedPlanObj.id}`,
                    planUid,
                    premium: {
                        insurer: 'bhp_indonesia',
                        plan: selectedPlanObj.id,
                        plan_label: selectedPlanObj.hasMaternity ? `${selectedPlanObj.label}${withMaternity ? ' (With Maternity)' : ''}` : selectedPlanObj.label,
                        included_benefits: '',
                        deductible: quoteDeductibles[quote.name] || '',
                        area_of_coverage: areaOfCoverage,
                        currency,
                        payment_frequency: paymentFrequency,
                        copay,
                        group_size: groupSize,
                        discount,
                        loading: loadingPercent,
                        with_maternity: withMaternity,
                        // Stored with discount/loading applied
                        premium_rate: typeof rate === 'number' ? rate : null,
                    },
                });
            });
        });
        const plan = {
            plan_id: `bhp_indonesia_${selectedPlan}`,
            uid: planUid,
            plan: selectedPlan,
            insurer: 'bhp_indonesia',
            label: `BHP Indonesia ${selectedPlanObj?.label || selectedPlan}`,
            payloads: {
                area_of_coverage: areaOfCoverage,
                currency,
                payment_frequency: paymentFrequency,
                copay,
                group_size: groupSize,
                discount,
                loading: loadingPercent,
            },
            deductibles_per_quote: Object.fromEntries(
                (quotes || []).map(quote => [quote.uid, quoteDeductibles[quote.name] || ''])
            ),
        };

        onAddToQuotations(withFreshRatesFlag(applyLeaveBlank(updates, (quotes || []).filter(isBlankQuote).map(q => q.uid)), fetchedFreshRatesRef.current), plan);
        toast.success(editingPlan ? 'Premiums Updated' : 'Premiums Added', { hideProgressBar: true, autoClose: 3000 });
        onClose();
    }

    const deductibleOptions = getDeductibles(currency);

    // The maternity toggle can be flipped to a variant that hasn't been fetched yet (most
    // likely right after opening in edit mode) — block saving until it's been re-fetched.
    const missingRateData = !!ratesData && selectedPlanObj.hasMaternity && (quotes || []).some(quote =>
        !isBlankQuoteRates(ratesData, quote.uid) && clients.some((client, idx) => getRate(idx, quote.uid) == null)
    );

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    maxHeight: '90vh',
                    backgroundColor: '#f1f4fa',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: BRAND_COLOR, px: 2, py: 1 }}>
                <div style={{
                    color: '#fff',
                    backgroundColor: '#0369ae',
                    width: 100,
                    height: 60,
                    padding: 6,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 13,
                    lineHeight: 1.2,
                }}>Business Health Plans</div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <img src={`${process.env.PUBLIC_URL}/images/Sunday Logo.png`} alt="Sunday Logo" style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    <img src={`${process.env.PUBLIC_URL}/images/BUPA Blue Logo.png`} alt="BUPA" style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    <IconButton size="small" onClick={onClose} sx={{ color: '#fff', ml: 1 }}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <ManualEditNotice quotations={quotations} editingPlan={editingPlan} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, rowGap: 2, mt: 3 }}>
                {/* Plan Type */}
                <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Plan Type</Typography>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                            value={selectedPlan}
                            disabled={loading || !!editingPlan}
                            // Rates for every plan come back in one fetch, so switching plans just shows a different row
                            onChange={(e) => setSelectedPlan(e.target.value)}
                            sx={{ fontSize: 12, height: 32 }}
                        >
                            {PLANS.map(plan => (
                                <MenuItem key={plan.id} value={plan.id} sx={{ fontSize: 12 }}>{plan.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {/* Options — label on the left, select on the right */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 170px', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        {copayOptions.length > 0 && (
                            <>
                                <Typography fontWeight={600} sx={{ fontSize: 13 }}>Copay Option</Typography>
                                <FormControl size="small" sx={{ width: 170 }}>
                                    <Select
                                        value={copay}
                                        disabled={loading}
                                        onChange={(e) => { setCopay(e.target.value); invalidateRates(); }}
                                        sx={{ fontSize: 12, height: 32 }}
                                    >
                                        {copayOptions.map(c => (
                                            <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        )}

                        {groupSizeOptions.length > 0 && (
                            <>
                                <Typography fontWeight={600} sx={{ fontSize: 13 }}>Group Size</Typography>
                                <FormControl size="small" sx={{ width: 170 }}>
                                    <Select
                                        value={groupSize}
                                        disabled={loading}
                                        onChange={(e) => { setGroupSize(e.target.value); invalidateRates(); }}
                                        sx={{ fontSize: 12, height: 32 }}
                                    >
                                        {groupSizeOptions.map(g => (
                                            <MenuItem key={g} value={g} sx={{ fontSize: 12 }}>{g}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </>
                        )}

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>MHD Loading</Typography>
                        <Typography fontSize={13} sx={{ color: '#1565c0' }}>{getDisplayValue('MHD Loading')}</Typography>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Group Size Discount</Typography>
                        <Typography fontSize={13} sx={{ color: '#1565c0' }}>{getDisplayValue('Group Size Discount')}</Typography>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Area Of Coverage</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select
                                value={areaOfCoverage}
                                disabled={loading}
                                onChange={(e) => { setAreaOfCoverage(e.target.value); invalidateRates(); }}
                                sx={{ fontSize: 12, height: 32 }}
                            >
                                {areaOptions.map(area => (
                                    <MenuItem key={area} value={area} sx={{ fontSize: 12 }}>{area}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Currency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select
                                value={currency}
                                disabled={loading}
                                onChange={(e) => setCurrency(e.target.value)}
                                sx={{ fontSize: 12, height: 32 }}
                            >
                                {currencyOptions.map(c => (
                                    <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Payment Frequency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select
                                value={paymentFrequency}
                                disabled={loading}
                                onChange={(e) => { setPaymentFrequency(e.target.value); invalidateRates(); }}
                                sx={{ fontSize: 12, height: 32 }}
                            >
                                {PAYMENT_FREQUENCIES.map(f => (
                                    <MenuItem key={f} value={f} sx={{ fontSize: 12 }}>{f}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                </Box>

                {/* Renewal/Further Discount & Loading */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Discount</Typography>
                        <PercentInput
                            value={discount}
                            onCommit={setDiscount}
                            disabled={loading}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Loading</Typography>
                        <PercentInput
                            value={loadingPercent}
                            onCommit={setLoadingPercent}
                            disabled={loading}
                        />
                </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Per-Quote Deductibles */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1.5, fontSize: 15 }}>Deductibles per Quote</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography sx={{ minWidth: 200, fontSize: 13, fontWeight: 600 }}>{quote.name}</Typography>
                                <CustomSelect2
                                    dense
                                    value={quoteDeductibles[quote.name] || ''}
                                    onChange={(e) => {
                                        setQuoteDeductibles(prev => ({ ...prev, [quote.name]: e.target.value }));
                                        setDeductibleErrors(prev => ({ ...prev, [quote.name]: false }));
                                        invalidateRates();
                                    }}
                                    items={[LEAVE_BLANK, ...deductibleOptions]}
                                    disabled={loading}
                                    error={!!deductibleErrors[quote.name]}
                                    sx={{ minWidth: 200 }}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* View Rates */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleViewRates}
                        disabled={loading || !selectedPlan || clients.length === 0}
                        size="small"
                        sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                        endIcon={<ManageSearchIcon />}
                    >
                        View Rates
                    </Button>
                </Box>

                {/* Rates Display */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: '#673ab7' }} />
                    </Box>
                )}

                {!loading && ratesData && (
                    <Box>
                        <Divider sx={{ mb: 2 }} />
                        {missingRateData && (
                            <Typography sx={{ mb: 1.5, fontSize: 13, color: '#f44336' }}>
                                Some "With Maternity" rates weren't loaded yet — click View Rates to fetch them before saving.
                            </Typography>
                        )}
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ mb: 3 }}>
                                <Typography fontWeight={600} sx={{ mb: 1 }}>
                                    {quote.name}
                                    <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: 'text.secondary', fontSize: 13 }}>
                                        — {quoteDeductibles[quote.name] || '-'}
                                    </Typography>
                                </Typography>
                                {isBlankQuoteRates(ratesData, quote.uid) ? <BlankQuoteNotice /> : (
<Paper variant="outlined" sx={{ overflowX: 'auto', backgroundColor: '#fff' }}>
                                    <Table size="small">
                                        <TableHead>
                                            {/* With Maternity — per client, only for plans that have a maternity variant.
                                                Both variants come back in one fetch, so toggling doesn't need a refresh. */}
                                            {selectedPlanObj.hasMaternity && (
                                                <TableRow sx={{ backgroundColor: MATERNITY_ROW_COLOR }}>
                                                    <TableCell />
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx}>
                                                            <FormControlLabel
                                                                sx={{ mb: 0, color: '#fff', whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: 12 } }}
                                                                control={
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={isMaternityIncluded(idx)}
                                                                        disabled={loading}
                                                                        onChange={(e) => setMaternityPerClient(prev => ({ ...prev, [idx]: e.target.checked }))}
                                                                        sx={{ py: 0, pl: 0, color: '#fff', '&.Mui-checked': { color: '#fff' } }}
                                                                    />
                                                                }
                                                                label="With Maternity"
                                                            />
                                                        </TableCell>
                                                    ))}
                                                    <TableCell />
                                                </TableRow>
                                            )}
                                            <TableRow sx={{ backgroundColor: BRAND_COLOR }}>
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 200 }}>Plan</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx} sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>
                                                        <div>{client.full_name || `Client ${idx + 1}`}{client.age ? ` (${client.age})` : ''}</div>
                                                        <div style={{ fontWeight: 400, fontSize: 12 }}>{client.country_of_residence || ''}</div>
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>Subtotal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                                                <TableCell sx={{ fontWeight: 500 }}>
                                                    {selectedPlanObj.label}
                                                </TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx}>
                                                        {formatRate(getRate(idx, quote.uid), currency)}
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ fontWeight: 600 }}>
                                                    {formatRate(getSubtotal(quote.uid), currency)}
                                                </TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </Paper>
)}
                            </Box>
                        ))}
                        <div ref={ratesEndRef} />
                    </Box>
                )}

                <NotesDisplay appConfig={appConfig} insurerId="bhp_indonesia" />

                <RatesVersionSelect
                    insurer="bhp_indonesia"
                    label="Rates Version"
                    insurerRates={insurerRates}
                    selectedRatesVersion={selectedRatesVersion}
                    setSelectedRatesVersion={setSelectedRatesVersion}
                    disabled={loading}
                    onChange={invalidateRates}
                    handleFetchAppConfig={handleFetchAppConfig}
                    onRatesVersionChanged={onRatesVersionChanged}
                />

                {/* Same "Click to Refresh" as Quotation1, pinned to the bottom of the scroll area */}
                <Box sx={{ position: 'sticky', bottom: 16, height: 0, zIndex: 2 }}>
                    <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>
                        <Zoom in={ratesStale && !loading} unmountOnExit>
                            <Fab
                                onClick={handleViewRates}
                                style={{
                                    backgroundColor: 'rgba(187, 222, 251, 0.55)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
                                    whiteSpace: 'nowrap',
                                }}
                                color='default'
                                variant="extended">
                                Click to Refresh
                                <RefreshIcon sx={{ ml: 1 }} />
                            </Fab>
                        </Zoom>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleAddToQuotations}
                    disabled={!ratesData || loading || missingRateData}
                    sx={{ display: ratesData ? 'inline-flex' : 'none', backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                    endIcon={editingPlan ? <SyncIcon /> : <AddIcon />}
                >{editingPlan ? 'Update Premiums' : 'Add Premiums'}</Button>
            </DialogActions>
        </Dialog>
    );
}
